import { uniqueId } from 'lodash'
import { Fragment, type ReactElement, useEffect, useState } from 'react'
import { Controller, type FieldPath, type FieldPathValue, type FieldValues } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import Button from '@mui/material/Button'
import FormControl from '@mui/material/FormControl'
import FormHelperText from '@mui/material/FormHelperText'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import { styled } from '@mui/material/styles'

import { getFieldErrorMessages } from 'global/helpers/form'
import { resizeImage } from 'global/helpers/imageTransformation'
import { formatBytes } from 'global/helpers/numberFormatters'
import { getRequestErrorMessage } from 'global/helpers/requestError'
import { validateImage } from 'global/helpers/validators'
import { type UploadFileResponse } from 'screens/StatusManager/StatusManager.service'
import KNFormFileItem from './KNFormFileItem'
import { type KNFormFileProps } from './types'

interface FileItem {
  id: string
  file: File
  pending: boolean
  value?: string | File | Promise<string | File>
  response?: UploadFileResponse
}

const InvisibleInput = styled('input')({
  display: 'none',
})

const KNFormFile = <T extends FieldValues>({
  name,
  control,
  setError,
  clearErrors,
  setValue,
  rules,
  label,
  size,
  sx,
  disabled,
  multiple,
  accept,
  processFile,
  minFileSize,
  maxFileSize,
  validateDocuments = false,
  onPending,
}: KNFormFileProps<T>): ReactElement => {
  const { t } = useTranslation()
  const [files, setFiles] = useState<FileItem[]>([])
  const [pending, setPending] = useState(false)
  const uniqueFieldId = uniqueId('field-')

  const getFileSizeMessage = (): string | undefined => {
    if (minFileSize && maxFileSize) {
      return t('form.validation.min_max_file_size', {
        min: formatBytes(minFileSize),
        max: formatBytes(maxFileSize),
      })
    } else if (minFileSize) {
      return t('form.validation.min_file_size', {
        min: formatBytes(minFileSize),
      })
    } else if (maxFileSize) {
      return t('form.validation.max_file_size', {
        max: formatBytes(maxFileSize),
      })
    }
    return undefined
  }

  const isValidFileFormat = (type: string): boolean => {
    if (!accept) {
      return true
    }
    return accept.includes(type)
  }

  const processPendingFiles = async (): Promise<void> => {
    clearErrors?.(name)
    const processedFiles = await Promise.all(
      files.map(async (file) => {
        if (file.pending) {
          if (validateImage(file.file.type)) {
            file.file = await resizeImage(file.file)
          }
          if (!isValidFileFormat(file.file.type)) {
            file.value = undefined
            setError?.(name, { message: t('form.validation.incorrect_file_format') })
            return file
          }
          if (!isValidFileSize(file)) {
            file.value = undefined
            setError?.(name, { message: getFileSizeMessage() })
            return file
          }
          try {
            if (processFile) {
              const processedFile = await processFile(file.file)
              file = updateProcessedFile(processedFile, file)
            } else {
              file.value = file.file
            }
          } catch (error) {
            file.value = undefined
            setError?.(name, { message: getRequestErrorMessage(error) })
          }
        }
        file.pending = false

        return file
      }),
    )
    const validFiles = processedFiles.filter((file) => Boolean(file.value))
    setFiles(validFiles)
    setPending(false)
    if (setValue) {
      // https://github.com/react-hook-form/react-hook-form/discussions/7246#discussioncomment-3101599
      if (validFiles.length === 0) {
        setValue(name, null as FieldPathValue<T, FieldPath<T>>)
      } else if (multiple) {
        setValue(name, validFiles.map((file) => file.value) as FieldPathValue<T, FieldPath<T>>)
      } else {
        setValue(name, validFiles[0]!.value as FieldPathValue<T, FieldPath<T>>)
      }
    }
  }

  const updateProcessedFile = (processedFile: File | UploadFileResponse, file: FileItem) => {
    if (processedFile instanceof File) {
      file.value = processedFile
    } else {
      file.value = processedFile.id
      file.response = processedFile
    }
    return file
  }

  const isValidFileSize = (file: FileItem) => {
    return ((minFileSize === undefined || file.file.size < minFileSize) && (maxFileSize === undefined || file.file.size > maxFileSize))
  }

  useEffect(() => {
    onPending?.(pending)
    if (pending) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      processPendingFiles()
    }
  }, [pending])

  useEffect(() => {
    if (disabled) {
      setFiles([])
      setPending(true)
    }
  }, [disabled])

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ fieldState: { invalid, error }, formState }): ReactElement => (
        <FormControl fullWidth sx={sx} error={invalid} disabled={formState.isSubmitting}>
          <label htmlFor={uniqueFieldId} style={{ alignSelf: 'flex-start' }}>
            <InvisibleInput
              id={uniqueFieldId}
              multiple={multiple}
              type="file"
              accept={accept?.join(',')}
              onChange={(event): void => {
                if (event.target.files) {
                  const newFiles = Array.from(event.target.files).map((file) => {
                    return {
                      id: uniqueId('file-'),
                      file,
                      pending: true,
                    }
                  })
                  setFiles(multiple ? [...files, ...newFiles] : newFiles)
                  setPending(true)
                }
                event.target.value = ''
              }}
              disabled={formState.isSubmitting || disabled}
            />
            <Button
              variant="contained"
              data-test={`form-file-${name}`}
              component="span"
              size={size}
              color="secondary"
              disabled={formState.isSubmitting || disabled}
            >
              {label ?? t('form.select_file')}
            </Button>
          </label>
          {files.length > 0 && (
            <List disablePadding>
              {files.map((file, index) => (
                <Fragment key={file.id}>
                  <KNFormFileItem
                    file={file.file}
                    pending={file.pending}
                    disabled={formState.isSubmitting || disabled}
                    onDelete={(): void => {
                      const newFiles = [...files]
                      newFiles.splice(index, 1)
                      setFiles(newFiles)
                      setPending(true)
                    }}
                  />
                  {validateDocuments && !file.pending && !file.response?.isDocument && (
                    <ListItem disableGutters disablePadding>
                      <FormHelperText error>{t('form.not_a_document')}</FormHelperText>
                    </ListItem>
                  )}
                </Fragment>
              ))}
            </List>
          )}
          {error?.message && <FormHelperText>{getFieldErrorMessages(error)}</FormHelperText>}
        </FormControl>
      )}
    />
  )
}

export default KNFormFile
